import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Toast } from 'primereact/toast';

import Dialog from '../../../shared/PFDialog/PFDialogContainer';
import PFInputText from '../../PFPrime/PFInputText';
import PFButton from '../../PFPrime/PFButton';
import PFAutoComplete from '../../../shared/PFPrime/PFAutoComplete.jsx';
import ApiService from '../../../../services/api.service';
import { homeDepotOrder, SOURCE_SYSTEMS } from '../../../../constants';
import PFCheckbox from '../../PFPrime/PFCheckbox';
import '../style/pullorder.css';

const COUNTDOWN_SECONDS = 10;

const PullOrder = ({ dailogVisible, setDailogVisible }) => {
  const { HD_ORDER, SF_AND_I_ORDER, HD_LEAD, BLINDS_ORDER } = homeDepotOrder;
  const history = useHistory();
  const toast = useRef(null);
  const storeRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const pendingRedirectRef = useRef(null);

  const [storeSearchResults, setStoreSearchResults] = useState([]);
  const [poNumber, setPoNumber] = useState('');
  const [storeValue, setStoreValue] = useState('');
  const [orderMark, setOrderMark] = useState(null);
  const [hdLead, setHdLead] = useState('');
  const [blindsOrder, setBlindsOrder] = useState('');
  const [mvendor, setMvendor] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isBusy = isSubmitting || countdown > 0;
  const actionLabel = orderMark === HD_LEAD ? 'Pull Lead' : 'Pull Order';

  const clearCountdownInterval = () => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  };

  const searchStore = async search => {
    try {
      const response = await ApiService.get(
        `/stores/reports?limit=10&offset=0&search_string=${search}`
      );
      return response;
    } catch (error) {
      console.error(error);
    }
  };

  const suggestStoreNumber = async event => {
    const response = await searchStore(event?.query || '');
    if (response && response?.items) {
      setStoreSearchResults(response?.items);
    }
  };

  const setStore = event => {
    setStoreValue(event?.target?.value);
  };

  const formValidation = () => {
    if (orderMark === HD_LEAD) {
      return !hdLead || !mvendor;
    }
    if (orderMark === BLINDS_ORDER) {
      return !blindsOrder;
    }
    return !poNumber?.length || !storeValue?.store_number;
  };

  const resetForm = useCallback(() => {
    clearCountdownInterval();
    pendingRedirectRef.current = null;
    setCountdown(0);
    setIsSubmitting(false);
    setPoNumber('');
    setStoreValue('');
    setHdLead('');
    setBlindsOrder('');
    setMvendor('');
  }, []);

  const startCountdown = () => {
    clearCountdownInterval();
    setCountdown(COUNTDOWN_SECONDS);
    countdownIntervalRef.current = setInterval(() => {
      setCountdown(prev => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
  };

  const pullOrders = async () => {
    setIsSubmitting(true);
    startCountdown();

    try {
      const payload = {
        source_system_id: SOURCE_SYSTEMS.HOME_DEPOT,
        order_mark: orderMark || HD_ORDER,
      };

      if (
        (orderMark === HD_ORDER ||
          orderMark === SF_AND_I_ORDER ||
          !orderMark) &&
        typeof storeValue === 'object'
      ) {
        payload.po_number = poNumber;
        payload.store_number = storeValue?.store_number
          ? `${storeValue.store_number}`
          : storeValue;
      }
      if (orderMark === HD_LEAD) {
        payload.hd_lead = hdLead;
        payload.mvendor = mvendor;
      }
      if (orderMark === BLINDS_ORDER) {
        payload.blinds_order = blindsOrder;
      }

      const response = await ApiService.post(
        `/rule-engine/pull-project-from-source`,
        payload
      );

      if (response && response?.status) {
        toast.current.show({
          severity: 'success',
          summary: 'Success',
          detail: `${response?.message}`,
          life: 4000,
        });
        pendingRedirectRef.current = `/admin/ruleenginelogs/${poNumber}`;
      }
    } catch (error) {
      console.error(error);
      toast.current.show({
        severity: 'error',
        summary: 'Something went wrong.',
      });
      pendingRedirectRef.current = null;
      clearCountdownInterval();
      setCountdown(0);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = () => {
    if (isBusy || formValidation()) {
      return;
    }
    pullOrders();
  };

  useEffect(() => {
    resetForm();
  }, [orderMark, resetForm]);

  useEffect(() => {
    if (countdown > 0) {
      return;
    }
    clearCountdownInterval();
  }, [countdown]);

  useEffect(() => {
    if (isSubmitting || countdown > 0) {
      return;
    }
    const redirectPath = pendingRedirectRef.current;
    if (redirectPath) {
      pendingRedirectRef.current = null;
      history.push(redirectPath);
    }
  }, [isSubmitting, countdown, history]);

  useEffect(() => {
    return () => {
      clearCountdownInterval();
    };
  }, []);

  return (
    <>
      <div>
        <Toast ref={toast} />
        <Dialog
          header="Pull Home Depot Project"
          visible={dailogVisible}
          className="w-8 xl:-5"
          onHide={() => {
            setDailogVisible(false);
            resetForm();
          }}
        >
          <div className="grid py-3">
            <div className="col-12 md:col-6 lg:col-3 mb-3 py-0">
              <PFCheckbox
                name="pull_project_home_depot"
                value={HD_ORDER}
                onChange={e => setOrderMark(e.target.value)}
                checked={orderMark === HD_ORDER || !orderMark}
                disabled={isBusy}
              />
              <label htmlFor={'pull_project_home_depot'} className="ml-2">
                {'HD Order'}
              </label>
            </div>
            <div className="col-12 md:col-6 lg:col-3 mb-3 py-0">
              <PFCheckbox
                name="pull_project_home_depot"
                value={SF_AND_I_ORDER}
                onChange={e => setOrderMark(e.target.value)}
                checked={orderMark === SF_AND_I_ORDER}
                disabled={isBusy}
              />
              <label htmlFor={'pull_project_home_depot'} className="ml-2">
                {'SF & I Order'}
              </label>
            </div>
            <div className="col-12 md:col-6 lg:col-3 mb-3 py-0">
              <PFCheckbox
                name="pull_project_home_depot"
                value={HD_LEAD}
                onChange={e => setOrderMark(e.target.value)}
                checked={orderMark === HD_LEAD}
                disabled={isBusy}
              />
              <label htmlFor={'pull_project_home_depot'} className="ml-2">
                {'HD Lead'}
              </label>
            </div>
            <div className="col-12 md:col-6 lg:col-3 mb-3 py-0">
              <PFCheckbox
                name="pull_project_home_depot"
                value={BLINDS_ORDER}
                onChange={e => setOrderMark(e.target.value)}
                checked={orderMark === BLINDS_ORDER}
                disabled={isBusy}
              />
              <label htmlFor={'pull_project_home_depot'} className="ml-2">
                {'Blinds Order'}
              </label>
            </div>
            {(orderMark === HD_ORDER ||
              !orderMark ||
              orderMark === SF_AND_I_ORDER) && (
              <>
                <div className="col-12 md:col-6 lg:col-6 mb-3 py-0">
                  <span className="p-float-label w-full">
                    <PFInputText
                      className="w-full"
                      id="po_number"
                      value={poNumber}
                      onChange={e => {
                        setPoNumber(e?.target?.value);
                      }}
                      maxLength={75}
                      disabled={isBusy}
                    />
                    <label htmlFor="po_number"> PO Number</label>
                  </span>
                </div>
                <div className="col-12 md:col-6 lg:col-6 mb-3 py-0">
                  <span className="p-float-label w-full">
                    <PFAutoComplete
                      ref={storeRef}
                      id="store_number"
                      className="w-full p-inputtext-sm"
                      suggestions={storeSearchResults}
                      field="store_number"
                      completeMethod={event => {
                        suggestStoreNumber(event);
                      }}
                      value={storeValue}
                      onChange={event => {
                        setStore(event);
                      }}
                      delay={500}
                      disabled={isBusy}
                    />
                    <label htmlFor="store_number">Store Number</label>
                  </span>
                </div>
              </>
            )}

            {orderMark === HD_LEAD && (
              <>
                <div className="col-6 md:col-6 lg:col-6 mb-3 py-0">
                  <span className="p-float-label w-full">
                    <PFInputText
                      className="w-full"
                      id="lead_number"
                      value={hdLead}
                      onChange={e => {
                        setHdLead(e?.target?.value);
                      }}
                      maxLength={10}
                      disabled={isBusy}
                    />
                    <label htmlFor="lead_number"> Lead #</label>
                  </span>
                </div>
                <div className="col-6 md:col-6 lg:col-6 mb-3 py-0">
                  <span className="p-float-label w-full">
                    <PFInputText
                      className="w-full"
                      id="mvendor"
                      value={mvendor}
                      onChange={e => {
                        setMvendor(e?.target?.value);
                      }}
                      maxLength={50}
                      disabled={isBusy}
                    />
                    <label htmlFor="MVendor">MVendor</label>
                  </span>
                </div>
              </>
            )}

            {orderMark === BLINDS_ORDER && (
              <>
                <div className="col-12 md:col-12 lg:col-12 mb-3 py-0">
                  <span className="p-float-label w-full">
                    <PFInputText
                      className="w-full"
                      id="order_id"
                      value={blindsOrder}
                      onChange={e => {
                        setBlindsOrder(e?.target?.value);
                      }}
                      maxLength={50}
                      disabled={isBusy}
                    />
                    <label htmlFor="order_id"> Order ID</label>
                  </span>
                </div>
              </>
            )}

            <div className="col-12 mt-3 py-0 flex justify-content-end align-items-center">
              <span className="w-full text-right flex align-items-center justify-content-end gap-2">
                {countdown > 0 && (
                  <span
                    key={countdown}
                    className="pull-order-countdown inline-flex align-items-center justify-content-center border-circle font-bold text-white"
                  >
                    {countdown}
                  </span>
                )}
                <PFButton
                  label={
                    countdown > 0
                      ? `${actionLabel} (${countdown})`
                      : actionLabel
                  }
                  onClick={handleSubmit}
                  loading={isBusy}
                  disabled={formValidation() || isBusy}
                />
              </span>
            </div>
          </div>
        </Dialog>
      </div>
    </>
  );
};

export default PullOrder;
